import Syncable from '@/syncing/Syncable.js';
import { useIncidentStore } from '@/store/incident';
import { useInstanceStore } from '@/store/backendInstances.js';
import _ from 'lodash';
import { canViewIncidentType } from '@/composables/permissionHelper';

class PenStateDurationSyncable extends Syncable {
  constructor(globalUniqueIdentifier) {
    super(
      globalUniqueIdentifier,
      1000 * 60 * 60, // 1 hour
      PenStateDurationSyncable.getSyncableHandle(),
      true,
      true,
    );
  }

  /**
   * Checks if the preconditions for syncing the pen state duration are satisfied.
   *
   * Preconditions:
   * - The instance type of the global unique identifier must be "barn".
   * - The super class must satisfy the preconditions (is online and logged in).
   *
   * @returns {boolean} - Returns true if the preconditions are satisfied, otherwise false.
   */
  satisfiesPreconditions() {
    if (
      useInstanceStore().instances[this.globalUniqueIdentifier]?.instanceType !==
      'barn'
    )
      return false;

    if (!canViewIncidentType('penState', this.globalUniqueIdentifier))
      return false;

    return super.satisfiesPreconditions();
  }

  hasInitialData() {
    return (
      useIncidentStore().penStateDuration[this.globalUniqueIdentifier] !== undefined
    );
  }

  /**
   * Performs the workload for syncing the pen state duration.
   *
   * The workload involves:
   * - Loading the latest pen state for the given global unique identifier.
   * - If no pen state is found, setting the pen state duration to null.
   *
   * - Loading the pen state duration for the latest pen state.
   * - If no pen state duration is found, exiting the function.
   *
   * - Comparing the loaded pen state duration with the current pen state duration.
   * - If they are not equal, updating the pen state duration in the incident store.
   *
   * - Loading the previous pen state and its duration.
   * - If the previous pen state is equal to the latest pen state, setting the previous pen state duration to null.
   * - If the previous pen state is not equal to the latest pen state, loading its duration.
   * - Updating the pen state duration in the incident store.
   * 
   * @async
   * @returns {Promise<void>} - A promise that resolves when the workload is complete.
   */
  async workload() {
    const penStates = {};

    // Load latest pen state and its duration
    const latestPenState = await useIncidentStore().loadLatestPenState(
      this.globalUniqueIdentifier,
    );
    if (!latestPenState) {
      /*
       * NOTE:
       * If there is no pen state, we set the pen state duration to null.
       * This will help when handling the loading state (undefined => loading=true, null => loading=false).
       */
      useIncidentStore().penStateDuration[this.globalUniqueIdentifier] = null;
      return;
    }

    const loadedPenStateDuration = useIncidentStore().loadPenStateDuration(latestPenState);
    if (!loadedPenStateDuration) {
      useIncidentStore().penStateDuration[this.globalUniqueIdentifier] = null;
      return;
    }

    const currentPenStateDuration = useIncidentStore().getPenStateDuration(this.globalUniqueIdentifier);
    if (
      currentPenStateDuration &&
      _.isEqual(currentPenStateDuration, loadedPenStateDuration)
    )
      return;

    penStates.current = loadedPenStateDuration;

    // Load previous pen state and its duration
    const previousPenState = await useIncidentStore().loadLatestClosedPenState(
      loadedPenStateDuration.incidentId,
      this.globalUniqueIdentifier,
    );
    if(!previousPenState) {
      penStates.previous = null;
      useIncidentStore().penStateDuration[this.globalUniqueIdentifier] = penStates;
      return;
    }

    if (
      previousPenState &&
      _.isEqual(previousPenState, latestPenState)
    ){
      penStates.previous = null;
      useIncidentStore().penStateDuration[this.globalUniqueIdentifier] = penStates;
      return;
    }
    
    const previousPenStateDuration = useIncidentStore().loadPenStateDuration(
      previousPenState,
    );

    penStates.previous = previousPenStateDuration;
    useIncidentStore().penStateDuration[this.globalUniqueIdentifier] = penStates;
  }

  static getSyncableHandle() {
    return 'pen-state-duration';
  }
}

export default PenStateDurationSyncable;
